import { useEffect, useRef, useState } from 'react'
import { Alert, Button, Link, Progress, Text } from '@/ui'
import { mtc, TextColors, TypographyStyles } from '@/styles'
import { getCurrentUser } from '@/selectors/user.selectors'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import {
  DONE_FOR_NOW,
  fetchCatchUpMonthStates,
  isReadableStatement,
  isSettled,
  keepLocalProgress,
  refusalFor,
  SLOT_KIND,
  toBankHistoryNote,
  toDisplay,
  toMonthDisplay,
  toOutcome,
  toPausedOutcome,
  toRequests,
  toSlots,
  uploadStatement,
  type Slot,
  type SlotState,
} from './statementUpload'
import type { WidgetProps } from './types'
import { StartMonthQuestion } from './StartMonthQuestion'

const UPLOAD_FAILED = 'The upload failed. The file is still selected.'
const HELP_CENTER_URL = 'https://support.joinheard.com/'

const SlotStatus = ({ state }: { state: SlotState }) => {
  if (state.kind === SLOT_KIND.uploading) {
    return (
      <div className="flex flex-col gap-1">
        <Progress
          value={state.percent}
          aria-label={`Uploading ${state.fileName}`}
        />
        <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
          Uploading · {state.percent}%
        </Text>
      </div>
    )
  }
  if (state.kind === SLOT_KIND.received) {
    return (
      <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
        Received · {state.fileName}
        {!state.readable && '. A bookkeeper will go through this one.'}
      </Text>
    )
  }
  if (state.kind === SLOT_KIND.onFile) {
    return (
      <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
        Already on file
      </Text>
    )
  }
  if (state.kind === SLOT_KIND.caughtUp) {
    return (
      <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
        Caught up
      </Text>
    )
  }
  if (state.kind === SLOT_KIND.error) {
    return (
      <Text variant={TypographyStyles.BODY_SM} color={TextColors.DANGER}>
        {state.message}
      </Text>
    )
  }
  return null
}

export const StatementUploadWidget = ({
  input,
  resolved,
  outcome,
  superseded = false,
  busy = false,
  onOutcome,
  onFollowUp,
}: WidgetProps) => {
  const dispatch = useAppDispatch()
  const user = useReselector(getCurrentUser)
  const fileInput = useRef<HTMLInputElement>(null)
  const pickingFor = useRef<string | null>(null)
  // Never reset: a refused report reopens the card, and re-sending on every
  // render would loop. "Done for now" is the retry.
  const autoReported = useRef(false)
  const [slots, setSlots] = useState<Slot[] | null>(null)
  const [uploadedThisTime, setUploadedThisTime] = useState(false)
  const [showHelp, setShowHelp] = useState(false)
  const [loadFailed, setLoadFailed] = useState(false)
  const [loadAttempt, setLoadAttempt] = useState(0)
  const [reopened, setReopened] = useState(false)
  const [uploadedSinceReopen, setUploadedSinceReopen] = useState(false)

  const answered = outcome !== undefined
  const canUpload = answered ? reopened && !busy && !superseded : !resolved
  const paused = answered && !reopened && !superseded

  // Keyed on content, not identity: a refetch mid-upload would reset every row.
  const requestKey = JSON.stringify(toRequests(input))

  useEffect(() => {
    const parsed: unknown = JSON.parse(requestKey)
    const requests = toRequests({ requests: parsed })
    let cancelled = false
    setLoadFailed(false)
    fetchCatchUpMonthStates()
      .then(
        (states) =>
          !cancelled &&
          setSlots((current) =>
            keepLocalProgress(current, toSlots(requests, states))
          )
      )
      .catch(() => !cancelled && setLoadFailed(true))
    return () => {
      cancelled = true
    }
  }, [requestKey, loadAttempt])

  const allSettled = Boolean(slots?.length) && Boolean(slots?.every(isSettled))

  useEffect(() => {
    if (
      slots &&
      allSettled &&
      uploadedThisTime &&
      !resolved &&
      !autoReported.current
    ) {
      autoReported.current = true
      onOutcome(toOutcome(slots))
    }
  }, [slots, allSettled, uploadedThisTime, resolved, onOutcome])

  useEffect(() => {
    if (
      slots &&
      allSettled &&
      reopened &&
      uploadedSinceReopen &&
      !busy &&
      !superseded
    ) {
      onFollowUp?.(toOutcome(slots))
      setUploadedSinceReopen(false)
      setReopened(false)
    }
  }, [
    slots,
    allSettled,
    reopened,
    uploadedSinceReopen,
    busy,
    superseded,
    onFollowUp,
  ])

  if (loadFailed) {
    return (
      <Alert
        variant="error"
        description="Heard couldn't load the statements it needs right now."
        ctaText="Try again"
        ctaOnClick={() => setLoadAttempt((attempt) => attempt + 1)}
      />
    )
  }
  if (!slots) return null

  const doneForNow = (
    <Button
      size="sm"
      variant="secondary"
      disabled={
        !canUpload ||
        slots.some(({ state }) => state.kind === SLOT_KIND.uploading)
      }
      onClick={() => {
        if (!answered) {
          onOutcome(toPausedOutcome(slots), toDisplay(slots))
          return
        }
        if (uploadedSinceReopen) onFollowUp?.(toOutcome(slots))
        setUploadedSinceReopen(false)
        setReopened(false)
      }}
    >
      {DONE_FOR_NOW}
    </Button>
  )

  const uploadMore = (
    <Button
      size="sm"
      variant="primary"
      disabled={busy}
      onClick={() => setReopened(true)}
    >
      Upload more statements
    </Button>
  )

  if (slots.length === 0) {
    return (
      <div className="flex flex-col gap-3 rounded-lg border border-neutral-200 p-4">
        <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
          Heard doesn&apos;t need a statement for any of these months right now.
        </Text>
        {!resolved && <div className="flex justify-end">{doneForNow}</div>}
      </div>
    )
  }

  const setSlotState = (key: string, state: SlotState) =>
    setSlots((current) =>
      (current ?? []).map((slot) =>
        slot.key === key ? { ...slot, state } : slot
      )
    )

  const upload = async (slot: Slot, file: File) => {
    if (!user) return
    const refusal = refusalFor(file)
    if (refusal) {
      setSlotState(slot.key, {
        kind: SLOT_KIND.error,
        message: refusal,
        file: null,
      })
      return
    }
    setSlotState(slot.key, {
      kind: SLOT_KIND.uploading,
      fileName: file.name,
      percent: 0,
    })
    const uploaded = await dispatch(
      uploadStatement(slot, file, user, (percent) =>
        setSlotState(slot.key, {
          kind: SLOT_KIND.uploading,
          fileName: file.name,
          percent,
        })
      )
    )
    if (!uploaded) {
      setSlotState(slot.key, {
        kind: SLOT_KIND.error,
        message: UPLOAD_FAILED,
        file,
      })
      return
    }
    setSlotState(slot.key, {
      kind: SLOT_KIND.received,
      fileName: file.name,
      readable: isReadableStatement(file),
    })
    setUploadedThisTime(true)
    if (answered) setUploadedSinceReopen(true)
  }

  const pickFor = (key: string) => {
    pickingFor.current = key
    fileInput.current?.click()
  }

  const onPicked = (files: FileList | null) => {
    const slot = slots.find(({ key }) => key === pickingFor.current)
    const file = files?.[0]
    pickingFor.current = null
    if (fileInput.current) fileInput.current.value = ''
    if (slot && file) void upload(slot, file)
  }

  const accounts = [...new Set(slots.map((slot) => slot.financialAccountId))]
  const nothingNeeded = slots.every(
    ({ state }) =>
      state.kind === SLOT_KIND.onFile || state.kind === SLOT_KIND.caughtUp
  )

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-neutral-200 p-4">
      {/* One hidden picker shared by every row's Upload and Replace buttons. */}
      <input
        ref={fileInput}
        type="file"
        className="hidden"
        aria-label="Choose a statement"
        onChange={(e) => onPicked(e.target.files)}
      />

      {nothingNeeded && (
        <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
          Heard has what it needs for these months.
        </Text>
      )}

      {accounts.map((financialAccountId, index) => {
        const accountSlots = slots.filter(
          (slot) => slot.financialAccountId === financialAccountId
        )
        const bankHistorySlot = accountSlots.find(
          (slot) => slot.beforeBankHistory
        )
        return (
          <div
            key={financialAccountId}
            className={mtc(
              'flex flex-col gap-2',
              index > 0 && 'mt-2 border-t border-neutral-200 pt-6'
            )}
          >
            <Text variant={TypographyStyles.LABEL_MD} color={TextColors.HEADER}>
              {accountSlots[0].accountLabel}
            </Text>
            {bankHistorySlot && (
              <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
                {toBankHistoryNote(
                  bankHistorySlot.institutionName,
                  bankHistorySlot.bankHistoryStartsOn
                )}
              </Text>
            )}
            {canUpload && (
              <StartMonthQuestion
                accountSlots={accountSlots}
                disabled={busy}
                onSaved={() => setLoadAttempt((attempt) => attempt + 1)}
              />
            )}
            {accountSlots.map((slot) => {
              const { state } = slot
              const monthDisplay = toMonthDisplay(slot.month)
              const canPick =
                (canUpload || paused) &&
                (state.kind === SLOT_KIND.open ||
                  state.kind === SLOT_KIND.error)
              return (
                <div
                  key={slot.key}
                  role="group"
                  aria-label={`${slot.accountLabel}, ${monthDisplay}`}
                  className={mtc(
                    'flex flex-col gap-1 border-t border-neutral-100 pt-2',
                    isSettled(slot) && 'opacity-80'
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <Text variant={TypographyStyles.BODY_MD}>
                      {monthDisplay}
                    </Text>
                    <div className="flex gap-2">
                      {state.kind === SLOT_KIND.error && state.file && (
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={!canUpload}
                          onClick={() =>
                            state.file && void upload(slot, state.file)
                          }
                        >
                          Retry
                        </Button>
                      )}
                      {canPick && (
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={!canUpload}
                          onClick={() => pickFor(slot.key)}
                        >
                          {state.kind === SLOT_KIND.error
                            ? 'Replace file'
                            : 'Upload'}
                        </Button>
                      )}
                      {state.kind === SLOT_KIND.error && (
                        <Button
                          variant="link"
                          size="sm"
                          disabled={!canUpload}
                          onClick={() =>
                            setSlotState(slot.key, { kind: SLOT_KIND.open })
                          }
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                  </div>
                  <SlotStatus state={state} />
                </div>
              )
            })}
          </div>
        )
      })}

      {!allSettled && showHelp && (
        <div className="flex flex-col gap-1 rounded-md bg-neutral-50 p-3">
          <Text variant={TypographyStyles.BODY_SM}>
            Log in to your bank on a computer and look for Statements,
            Documents, or Paperless, usually near your account details. Download
            each month as a PDF and upload it here.
          </Text>
          <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
            Still can&apos;t find one?{' '}
            <Link
              href={HELP_CENTER_URL}
              newPage
              trackMessage="clicked help center on statement upload card"
            >
              Ask a person at the Help Center
            </Link>
            .
          </Text>
        </div>
      )}

      {paused && !allSettled && (
        <div className="flex justify-end">{uploadMore}</div>
      )}

      {canUpload && (
        <div className="flex items-center justify-between gap-2">
          {allSettled ? (
            <span />
          ) : (
            <Button
              variant="link"
              size="sm"
              className="px-0"
              aria-expanded={showHelp}
              onClick={() => setShowHelp(!showHelp)}
            >
              Not sure where to find these?
            </Button>
          )}
          {doneForNow}
        </div>
      )}
    </div>
  )
}
